import { useQuery } from "@tanstack/react-query";
import { Box, Typography } from "@mui/material";
import React, { useContext } from "react";

import makeStyles from "@mui/styles/makeStyles";

import TagsService from "../../services/TagsService";

import { ConnectionPagination } from "../pagination/ConnectionPagination";
import { ManageTagDialog } from "../exercise-library/tags/ManageTagDialog";
import { TagSkeletonCard } from "../card/ExerciseTagSkeletonCard";
import TagCard from "../exercise-library/tags/TagCard";
import { InfoBox } from "../info/InfoBox";
import { CreateTagAction } from "../exercise-library/buttons/CreateExerciseTagButton";
import { TagType } from "../../constants";
import { CoachClientFiltersContext } from "../../contexts/CoachClientFiltersContext";
import { TagDto, TagSort } from "@growth-machine-llc/stridist-api-client";
import OptimisticUpdateContainer from "../loading/OptimisticUpdateContainer";

export const COACH_CLIENT_TAGS_LIST_QUERY_KEY = "coach-client-tags-list";

const MAX_WIDTH = 400;

const useStyles = makeStyles(() => ({
  createTagButton: {
    width: "100%",
    maxWidth: MAX_WIDTH,
  },
}));

const ITEMS_PER_PAGE = 5;

export const CoachClientTagsListRoute = () => {
  const s = useStyles();
  const [page, setPage] = React.useState(1);
  const perPage = ITEMS_PER_PAGE;
  const handlePageChange = React.useCallback((cursor: number) => {
    setPage(cursor);
  }, []);
  const { query, sortKey } = useContext(CoachClientFiltersContext);

  const { data: coachClientsTags, isLoading } = useQuery({
    queryKey: [
      COACH_CLIENT_TAGS_LIST_QUERY_KEY,
      { page, perPage, query, sortKey },
    ],
    queryFn: () =>
      TagsService.getTagsPaginated(
        page,
        perPage,
        query,
        sortKey as unknown as TagSort,
      ),
  });
  const [manageTagDialogOpen, setManageTagDialogOpen] = React.useState(false);
  const [selectedClientTag, setSelectedClientTag] =
    React.useState<TagDto | null>();
  const handleCloseExerciseTagDialog = React.useCallback(() => {
    setManageTagDialogOpen(false);
    setSelectedClientTag(null);
  }, []);
  return (
    <Box display={"flex"} flexDirection="column" gap={2}>
      {isLoading || !coachClientsTags ? (
        <>
          {Array.from({ length: 5 }).map((_, index) => (
            <TagSkeletonCard key={index} />
          ))}
        </>
      ) : coachClientsTags.items.length > 0 ? (
        coachClientsTags.items.map((tag) => (
          <OptimisticUpdateContainer id={tag.id} key={tag.id}>
            <TagCard
              tag={tag}
              onEdit={() => {
                setSelectedClientTag(tag);
                setManageTagDialogOpen(true);
              }}
              onClick={() => {
                setSelectedClientTag(tag);
                setManageTagDialogOpen(true);
              }}
              tagType={TagType.CLIENT}
              key={tag.id}
            />
          </OptimisticUpdateContainer>
        ))
      ) : coachClientsTags.totalCount > 0 ? (
        <Box display={"flex"} justifyContent={"center"} padding={2}>
          <Typography> No tags found for current filters.</Typography>
        </Box>
      ) : (
        <>
          <InfoBox
            title={"No exercise tags found"}
            subtitle={"Create a tag to classify you exercises."}
          >
            <CreateTagAction
              useThemePalette={false}
              className={s.createTagButton}
              tagType={TagType.CLIENT}
            />
          </InfoBox>
        </>
      )}
      {manageTagDialogOpen && (
        <ManageTagDialog
          open={manageTagDialogOpen}
          tag={selectedClientTag}
          onClose={handleCloseExerciseTagDialog}
          tagType={TagType.CLIENT}
        />
      )}
      <ConnectionPagination
        currentPage={page || 1}
        pages={coachClientsTags?.totalPages || 1}
        gotoPage={handlePageChange}
        autoHide
      />
    </Box>
  );
};
